'use client';

import { getBunnyPreviewUrl } from '@/shared/constants/bunny';
import { useEffect, useRef, useState, type ReactElement } from 'react';

interface BunnyHoverPreviewProps {
  bunnyVideoId: string;
}

/**
 * Card roots whose `:hover` / `:focus-within` fades the preview in — the same
 * selectors as `.tb-bunny-preview` in `features/cards/primitives.css` and the
 * AI-chat overrides in `app/globals.css`. Keep the three lists in sync.
 */
const HOVER_ROOT_SELECTOR = '.tbCard, .vcard, .featured, .ai-featured';

/**
 * Picks the Bunny hover-preview overlay when a `bunny_video_id` is
 * present and the card isn't locked. Shared across all video-like card
 * containers (video, short, episode) so the tiering logic stays in one
 * place.
 */
export function buildBunnyOverlay(
  wantsBunny: boolean,
  bunnyVideoId: string | undefined
): ReactElement | null {
  if (!wantsBunny) return null;
  if (typeof bunnyVideoId !== 'string' || bunnyVideoId.length === 0) return null;
  return <BunnyHoverPreview bunnyVideoId={bunnyVideoId} />;
}

/**
 * Cheap hover motion for card thumbs — uses Bunny's pre-generated
 * `preview.webp`. Static image overlay; CSS on `.tbCard:hover` fades it
 * in (see `.tb-bunny-preview` in `features/cards/primitives.css`).
 *
 * The `src` is only set on the first mouse/pen hover or keyboard focus of
 * the card. Each webp is 0.3–3.4 MB and `loading="lazy"` does not hold back
 * cards parked off-screen inside horizontal carousels, so an eager `src`
 * cost a card-heavy page tens of MB before anyone hovered (FRONT-532).
 * Touch never arms: it has no hover, so the asset could never be seen.
 *
 * Silently removes itself when Bunny hasn't generated the asset yet —
 * the `onError` handler flips `failed` to true and the component returns
 * `null`, leaving the base thumbnail visible.
 */
export function BunnyHoverPreview({ bunnyVideoId }: BunnyHoverPreviewProps): ReactElement | null {
  const imgRef = useRef<HTMLImageElement>(null);
  const [armed, setArmed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (armed) return;
    const img = imgRef.current;
    const root = img?.closest(HOVER_ROOT_SELECTOR) ?? img?.parentElement;
    if (!root) return;

    const arm = (): void => setArmed(true);
    const handlePointerEnter = (event: Event): void => {
      if ((event as PointerEvent).pointerType === 'touch') return;
      arm();
    };

    root.addEventListener('pointerenter', handlePointerEnter, { passive: true });
    root.addEventListener('focusin', arm);
    return () => {
      root.removeEventListener('pointerenter', handlePointerEnter);
      root.removeEventListener('focusin', arm);
    };
  }, [armed]);

  if (failed) return null;

  return (
    <img
      ref={imgRef}
      {...(armed ? { src: getBunnyPreviewUrl(bunnyVideoId) } : {})}
      alt=""
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      className="tb-bunny-preview"
      style={{ visibility: loaded ? 'visible' : 'hidden' }}
    />
  );
}
